import axios from 'axios';
import config from './env';

const createOfflineError = () => ({
  message: 'No Internet Connection Found',
  code: 'OFFLINE',
  isOfflineError: true,
});

let isRedirectingToLogin = false;

// Debounce connectivity checks so multiple failing API calls don't spam requests
let lastConnectivityCheck = 0;
let lastConnectivityResult = true;
const CONNECTIVITY_CHECK_COOLDOWN = 5000; // 5 seconds

/**
 * Verify actual internet connectivity by trying to reach an external server.
 * navigator.onLine is unreliable on Windows with virtual adapters (Hyper-V, WSL),
 * so we need to perform a real network request.
 */
const checkConnectivity = async () => {
  // Fast path: if the browser itself says offline, trust it
  if (typeof navigator !== 'undefined' && navigator.onLine === false) {
    lastConnectivityResult = false;
    return false;
  }

  // Return cached result if we checked recently (debounce)
  const now = Date.now();
  if (now - lastConnectivityCheck < CONNECTIVITY_CHECK_COOLDOWN) {
    return lastConnectivityResult;
  }
  lastConnectivityCheck = now;

  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 5000);
    await fetch('https://www.google.com/generate_204', {
      method: 'HEAD',
      mode: 'no-cors',
      cache: 'no-store',
      signal: controller.signal,
    });
    clearTimeout(timeoutId);
    lastConnectivityResult = true;
    return true;
  } catch {
    lastConnectivityResult = false;
    return false;
  }
};

const handleAxiosError = async (err, pathname, isAuthPath) => {
  const { response } = err;

  // Network error — verify if the user is truly offline before showing No Internet page.
  // This prevents API server issues (CORS, server down) from triggering the offline screen.
  if (!response && (err?.code === 'ERR_NETWORK' || err?.message === 'Network Error')) {
    const isOnline = await checkConnectivity();
    if (!isOnline) {
      window.dispatchEvent(new Event('app-offline'));
      throw createOfflineError();
    }
  }

  if (response && response.status === 401 && !isAuthPath.has(pathname) && !isRedirectingToLogin) {
    isRedirectingToLogin = true;
    localStorage.clear();
    globalThis.window.location.replace('/login');
  }
  throw err;
};

const request = ({
  url,
  method,
  data,
  params,
  headers,
  responseType,
  baseUrl,
  sendRes,
  onUploadProgress,
  timeout,
  signal,
}) => {
  const browserAgent =
    navigator?.userAgentData?.brands
      ?.map((item) => `"${item.brand}";v="${item.version}"`)
      ?.join(', ') || '';

  const token = localStorage.getItem('accessToken');
  const isAuthPath = new Set(['/login', '/forgotPassword', '/reset-password', '/verify-code']);

  const { location } = globalThis.window;
  const { pathname } = location;

  // Use baseUrl if provided, otherwise fallback to config.apiUrl
  const apiBase = baseUrl || config.apiUrl;

  if (typeof navigator !== 'undefined' && navigator.onLine === false) {
    window.dispatchEvent(new Event('app-offline'));
    return Promise.reject(createOfflineError());
  }

  const axiosConfig = {
    url: apiBase + url,
    method,
    data: data || null,
    params: params || null,
    responseType,
    onUploadProgress,
    ...(timeout !== undefined && { timeout }),
    ...(signal && { signal }),
    headers: {
      Authorization: token ? `Bearer ${token}` : '',
      'Content-Type': 'application/json',
      Accept: 'application/json',
      platform: 'web',
      ...headers,
    },
  };

  return axios(axiosConfig)
    .then((res) => {
      if (sendRes) {
        return res;
      } else {
        return res.data;
      }
    })
    .catch((err) => handleAxiosError(err, pathname, isAuthPath));
};

export default request;
